import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react"

type TableContextType = {
  getColor: (originalIdx: number) => string
  getDisplayIndex: (originalIdx: number) => number
  hasColor: (originalIdx: number) => boolean
  setColorMap: (m: Map<number, string>) => void
  setIndexMap: (m: Map<number, number>) => void
}

const TableContext = createContext<TableContextType>({
  getColor: () => 'black',
  getDisplayIndex: () => 0,
  hasColor: () => false,
  setColorMap: () => { },
  setIndexMap: () => { }
})

export const TableProvider = ({ children }: { children: ReactNode }) => {
  const [colorMap, setColorMapState] = useState<Map<number, string>>(new Map())
  const [indexMap, setIndexMapState] = useState<Map<number, number>>(new Map())

  const getColor = useCallback((originalIdx: number) => colorMap.get(originalIdx) ?? 'black', [colorMap])
  const hasColor = useCallback((originalIdx: number) => colorMap.has(originalIdx), [colorMap])
  const getDisplayIndex = useCallback((originalIdx: number) => indexMap.get(originalIdx) ?? 0, [indexMap])
  const setColorMap = useCallback((m: Map<number, string>) => setColorMapState(m), [])
  const setIndexMap = useCallback((m: Map<number, number>) => { setIndexMapState(m) }, [])

  const value = useMemo(
    () => ({
      getColor,
      hasColor,
      getDisplayIndex,
      setColorMap,
      setIndexMap
    })
    , [getColor, hasColor, getDisplayIndex, setColorMap, setIndexMap])

  return (
    <TableContext.Provider value={value}>
      {children}
    </TableContext.Provider>
  )
}

export const useTableContext = () => useContext(TableContext)